import { useEffect, useRef, useState } from "react";
import { Bell, CheckCheck, Circle, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../utils/api";

function formatNotificationTime(value) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function levelClass(level) {
  if (level === "success") return "bg-emerald-100 text-emerald-700";
  if (level === "warning") return "bg-amber-100 text-amber-700";
  if (level === "error") return "bg-rose-100 text-rose-700";
  return "bg-slate-100 text-slate-600";
}

function normalizeActionUrl(url) {
  if (!url) return null;

  return url.replace(/\/jobs\/(\d+)\/details$/, "/jobs/$1");
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await apiFetch("/notifications?limit=10");
      if (!response.ok) return;
      const data = await response.json();
      setUnreadCount(data.unread_count || 0);
      setNotifications(data.notifications || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const togglePanel = () => {
    setOpen((value) => {
      const nextValue = !value;
      if (nextValue) {
        loadNotifications();
      }
      return nextValue;
    });
  };

  const markAllRead = async () => {
    const response = await apiFetch("/notifications/read-all", { method: "POST" });
    if (response.ok) {
      setUnreadCount(0);
      setNotifications((items) => items.map((item) => ({ ...item, is_read: true })));
    }
  };

  const openNotification = async (notification) => {
    if (!notification.is_read) {
      await apiFetch(`/notifications/${notification.id}/read`, { method: "POST" });
      setUnreadCount((count) => Math.max(0, count - 1));
      setNotifications((items) =>
        items.map((item) =>
          item.id === notification.id ? { ...item, is_read: true } : item
        )
      );
    }

    const actionUrl = normalizeActionUrl(notification.action_url);
    if (actionUrl) {
      setOpen(false);
      navigate(actionUrl);
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={togglePanel}
        className="relative flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800"
        aria-label="Recent activities"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 min-w-[18px] rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[22rem] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <div>
              <p className="text-sm font-bold text-slate-950">Recent Activities</p>
              <p className="text-xs text-slate-500">Latest recruiter activity at a glance</p>
            </div>
            <button
              type="button"
              onClick={markAllRead}
              disabled={!unreadCount}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:text-slate-300"
            >
              <CheckCheck size={14} />
              Read all
            </button>
          </div>

          <div className="max-h-[26rem] overflow-y-auto p-2">
            {loading && notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-500">
                Loading notifications...
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-500">
                No notifications yet.
              </div>
            ) : (
              notifications.map((notification) => (
                <button
                  key={notification.id}
                  type="button"
                  onClick={() => openNotification(notification)}
                  className="mb-1 flex w-full gap-3 rounded-xl px-3 py-3 text-left transition hover:bg-slate-50"
                >
                  <span className={`mt-0.5 rounded-full p-1.5 ${levelClass(notification.level)}`}>
                    <Circle size={8} fill="currentColor" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-start justify-between gap-3">
                      <span className="text-sm font-semibold text-slate-950">
                        {notification.title}
                      </span>
                      {!notification.is_read && (
                        <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                      )}
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-slate-600">
                      {notification.message}
                    </span>
                    <span className="mt-2 flex items-center justify-between text-[11px] font-medium uppercase tracking-wide text-slate-400">
                      {formatNotificationTime(notification.created_at)}
                      {normalizeActionUrl(notification.action_url) && <ExternalLink size={12} />}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
